import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';

import type { ApiWrapped } from '@core/http/api-response.types';
import { apiUrl } from '@core/http/api-url.util';
import { unwrapApiData } from '@core/http/unwrap-api-data.util';

export type PaymentPlanId = 'payg' | 'pack10' | 'template' | 'tools';

export type CreateOrderResponse = {
  order_id: string;
  payment_session_id: string;
  amount: number;
  plan_type: 'PAYG' | 'PACK10' | 'JOB_SEARCH' | 'TEMPLATE' | 'TOOLS';
  /** Must match server `CASHFREE_MODE` or Cashfree.js rejects the session id. */
  cashfree_checkout_mode?: 'sandbox' | 'production';
  discount_amount?: number;
  original_amount?: number;
  affiliate_code?: string | null;
  template_id?: string | null;
};

export type ApplyAffiliateCodeResponse = {
  affiliateId: string;
  affiliateCode: string;
  originalAmount: number;
  discountAmount: number;
  payableAmount: number;
  commissionPreview: number;
};

export type PaymentStatusResponse = { status: string };

type Wrapped<T> = { success: true; data: T };

/** `?intent=` on `/login` when user must sign in with Google before checkout. */
export const LOGIN_INTENT_PURCHASE = 'purchase';

/** After the last onboarding step: user signs in with Google, then we POST saved draft to `/onboarding`. */
export const LOGIN_INTENT_COMPLETE_ONBOARDING = 'complete_onboarding';

/** Landing demo finished: sign in with Google, then complete the real onboarding flow. */
export const LOGIN_INTENT_GUEST_UPGRADE = 'guest_upgrade';

/** Safe post-auth return path for Premium (internal route only). */
export const LOGIN_NEXT_PREMIUM = '/dashboard/premium';

/** 403 when payment requires a linked account (`GUEST_MUST_SIGN_UP`: legacy error code). */
export function isGuestMustSignUpPaymentError(err: unknown): boolean {
  if (!(err instanceof HttpErrorResponse)) return false;
  const d = err.error?.error?.details as { error?: string } | Record<string, unknown> | undefined;
  const code =
    typeof d === 'object' && d !== null && 'error' in d && typeof (d as { error: unknown }).error === 'string'
      ? (d as { error: string }).error
      : undefined;
  return err.status === 403 && code === 'GUEST_MUST_SIGN_UP';
}

@Injectable({ providedIn: 'root' })
export class PaymentApiService {
  private readonly http = inject(HttpClient);

  createOrder(
    plan: PaymentPlanId,
    opts?: {
      affiliateCode?: string | null;
      templateId?: string | null;
      durationMinutes?: number | null;
      atsQty?: number;
      rewriteQty?: number;
    },
  ): Observable<CreateOrderResponse> {
    const body: {
      plan: PaymentPlanId;
      affiliate_code?: string;
      template_id?: string;
      duration_minutes?: number;
      ats_qty?: number;
      rewrite_qty?: number;
    } = { plan };
    const code = opts?.affiliateCode?.trim();
    if (code) body.affiliate_code = code;
    const templateId = opts?.templateId?.trim();
    if (templateId) body.template_id = templateId;
    const duration = opts?.durationMinutes;
    if (typeof duration === 'number' && Number.isFinite(duration) && duration > 0) {
      body.duration_minutes = Math.round(duration);
    }
    if (plan === 'tools') {
      body.ats_qty = opts?.atsQty ?? 0;
      body.rewrite_qty = opts?.rewriteQty ?? 0;
    }
    return this.http
      .post<ApiWrapped<CreateOrderResponse>>(apiUrl('/payment/create-order'), body)
      .pipe(
        unwrapApiData(),
        catchError((err: HttpErrorResponse) => {
          const details = err.error?.error?.details as
            | { error?: string; message?: string; interview_credits_remaining?: number }
            | undefined;
          if (isGuestMustSignUpPaymentError(err)) {
            return throwError(() => Object.assign(new Error('GUEST_MUST_SIGN_UP'), { code: 'GUEST_MUST_SIGN_UP' }));
          }
          if (
            err.status === 409 &&
            details?.error === 'INTERVIEW_CREDITS_REMAINING' &&
            typeof details.interview_credits_remaining === 'number'
          ) {
            return throwError(
              () =>
                new Error(
                  `You have ${details.interview_credits_remaining} paid interview credit(s) left. Start an interview from the dashboard first.`,
                ),
            );
          }
          const msg =
            typeof details?.message === 'string'
              ? details.message
              : (err.error?.error?.message as string) || err.message;
          return throwError(() => new Error(msg || 'Could not start payment.'));
        }),
      );
  }

  /** Preview affiliate discount for a plan (runtime validation before checkout). */
  applyAffiliateCode(
    code: string,
    plan: 'payg' | 'pack10' | 'tools',
    opts?: { atsQty?: number; rewriteQty?: number },
  ): Observable<ApplyAffiliateCodeResponse> {
    const body: Record<string, unknown> = { code: code.trim().toUpperCase(), plan };
    if (plan === 'tools') {
      body['ats_qty'] = opts?.atsQty ?? 0;
      body['rewrite_qty'] = opts?.rewriteQty ?? 0;
    }
    return this.http
      .post<ApiWrapped<ApplyAffiliateCodeResponse>>(apiUrl('/affiliates/apply-code'), body)
      .pipe(
        unwrapApiData(),
        catchError((err: HttpErrorResponse) => {
          const msg =
            (err.error?.error?.message as string) ||
            (typeof err.error?.message === 'string' ? err.error.message : null) ||
            'Invalid code';
          return throwError(() => new Error(msg));
        }),
      );
  }

  getPaymentStatus(orderId: string, sync = false): Observable<PaymentStatusResponse> {
    const q = sync ? '?sync=true' : '';
    return this.http
      .get<ApiWrapped<PaymentStatusResponse>>(apiUrl(`/payment/status/${orderId}${q}`))
      .pipe(unwrapApiData());
  }

}
